import React, { useEffect, useState } from 'react';
import { Outlet, Route, BrowserRouter as Router, Routes, useNavigate } from 'react-router-dom';
import './App.css';
import Overview from './pages/Overview';
import Header from './components/Header';
import ApiKeys from './pages/ApiKeys';
import Profile from './pages/Profile';
import OverviewIcon from '@/assets/icons/Overview.svg';
import LockIcon from '@/assets/icons/Lock.svg';
import UserIcon from '@/assets/icons/User.svg';
import BillingIcon from '@/assets/icons/Billing.svg';
import BellDotIcon from '@/assets/icons/BellDot.svg';
import EnhancedNavLink from './components/EnhancedNavLink';
import { cn } from './lib/utils';
import Billing from './pages/Billing';
import { getInvoices, getOrganization } from './service/overview';
import Signup from './pages/Signup';
import { Toaster } from 'sonner';
import Notification from './pages/Notification';
import AnimateFavIcon from '@jenmusic/animate-favicon';
import { useDimensions } from './hooks/useDimensions';

const LOGIN_MAP: {
  [key: string]: string;
} = {
  'https://api-platform-local.futureverseai.com': 'https://jen-local.futureverseai.com',
  'https://platform-dev.jenmusic.tech': 'https://dev.jenmusic.tech',
  'https://platform-staging.jenmusic.tech': 'https://staging.jenmusic.tech',
  'https://platform.jenmusic.ai': 'https://app.jenmusic.ai',
};

const App: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      getInvoices().catch((err) => {
        const errorMessage = err.message;
        const redirectUrl = LOGIN_MAP[window.location.origin];
        if (redirectUrl) {
          if (errorMessage === 'Forbidden resource' || errorMessage === 'Unauthorized') {
            window.location.href = `${redirectUrl}/logout?loginCallbackUrl=${window.location.href}`;
            throw new Error('need login');
          }
        }
      }),
      getOrganization().then(({ contact_name }) => {
        if (!contact_name && window.location.pathname !== '/signup') {
          window.location.href = '/signup';
          throw new Error('need signup');
        }
      }),
    ]).then(() => {
      setIsLoading(false);
    });
  }, []);

  if (isLoading) {
    return null;
  }

  return (
    <div className={cn('w-full min-h-full pb-5 flex flex-col bg-[#f7f7f7]', { dark: false })}>
      <Router>
        <main className="flex h-ful flex-1">
          <Routes>
            <Route path="/" element={<DefaultLayout />}>
              <Route path="/" element={<Overview />} />
              <Route path="/api-keys" element={<ApiKeys />} />
            </Route>
            <Route path="/" element={<ProfileLayout />}>
              <Route path="/profile" element={<Profile />} />
              <Route path="/billing" element={<Billing />} />
              <Route path="/notification" element={<Notification />} />
            </Route>
            <Route path="/">
              <Route path="/signup" element={<Signup />} />
            </Route>
          </Routes>
        </main>
      </Router>
      <Toaster />

      <AnimateFavIcon
        sequences={new Array(18).fill(0).map((_, index) => require(`@/assets/favicons/${index + 1}.png`))}
        gap={200}
        loop={2}
      />
    </div>
  );
};

const DefaultLayout = () => {
  const { isMobileOrPad } = useDimensions();
  return (
    <div className="flex flex-col w-full">
      <Header className="" />
      <div className=" h-full flex w-full">
        {!isMobileOrPad && (
          <div className="w-[248px]">
            <DefaultNav />
          </div>
        )}

        <main className="mr-2 mb-2 lg:flex-1 w-[calc(100vw-16px)] mx-auto bg-white rounded-lg border border-jen-border-primary">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export const DefaultNav = () => {
  return (
    <>
      <div className="text-jen-content-tertiary font-pressura-mono text-[12px]  py-2 px-4">API DASHBOARD</div>
      <div className="px-2 flex flex-col">
        <EnhancedNavLink to={'/'}>
          <OverviewIcon fillColor="currentColor" />
          Overview
        </EnhancedNavLink>
        <EnhancedNavLink to={'/api-keys'}>
          <LockIcon fillColor="currentColor" /> API Keys
        </EnhancedNavLink>
      </div>
    </>
  );
};
const ProfileLayout = () => {
  const { isMobileOrPad } = useDimensions();

  return (
    <div className="flex flex-col w-full">
      <Header className="" />
      <div className=" h-full flex w-full">
        {!isMobileOrPad && (
          <div className="w-[248px]">
            <ProfileNav />
          </div>
        )}

        <main className="mr-2 mb-2 lg:flex-1 w-[calc(100vw-16px)] mx-auto bg-white rounded-lg border border-jen-border-primary">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export const ProfileNav = () => {
  return (
    <>
      <div className="text-jen-content-tertiary font-pressura-mono text-[12px]  py-2 px-4"> SETTINGS</div>
      <div className="px-2 flex flex-col">
        <EnhancedNavLink to={'/profile'}>
          <UserIcon fillColor="currentColor" /> Profile
        </EnhancedNavLink>
        <EnhancedNavLink to={'/billing'}>
          <BillingIcon fillColor="currentColor" /> Billing
        </EnhancedNavLink>
        {/* <EnhancedNavLink to={'/notification'}>
          <BellDotIcon fillColor="currentColor" /> Notifications
        </EnhancedNavLink> */}
      </div>
    </>
  );
};

export default App;
